import { getPackagesList } from '@/api/package';
import { PackageAPI } from '@/api/type';
import React, { useState, useMemo, useEffect } from 'react';
interface ProfitcalcPackage {
    default: string;
}
const ProfitCalculator: React.FC<ProfitcalcPackage> = (props) => {
    useEffect(() => {
        getPackagesList().then((res) => {
            setPACKAGES(res.data
                .filter((pkg: PackageAPI) => pkg.part_per_user !== 0)
                .map((pkg: PackageAPI) => ({
                    name: pkg.name,
                    value: Number(pkg.price),
                    weight: pkg.part_per_user,
                    minPercent: Number(pkg.min_profit_rate) / 100
                })));
        });
    }, []);

    // 1. Định nghĩa danh sách các gói đầu tư
    const [PACKAGES, setPACKAGES] = useState([
        { name: "PIONEER", value: 300000000, weight: 1, minPercent: 0.03 },
        { name: "TITAN", value: 1500000000, weight: 6, minPercent: 0.03 },
    ]);

    const defaultPkgIndex = props.default.toUpperCase() === 'TITAN' ? 0 : 1;

    // 2. State quản lý vị trí slider (gói đầu tư)
    const [pkgIndex, setPkgIndex] = useState(defaultPkgIndex); // Mặc định gói Pioneer

    // Các thông số hệ thống cố định
    const FIXED_REVENUE = 1; // Doanh thu giả định 100 Tỷ
    const TOTAL_WEIGHT = 210;
    const DURATION = 36;

    const selectedPkg = PACKAGES[pkgIndex];

    // 3. Logic tính toán
    const calculations = useMemo(() => {
        const revenueVND = FIXED_REVENUE * 1000000000;
        const sharePool = revenueVND * 0.08;
        const valuePerWeight = sharePool / TOTAL_WEIGHT;
        const profitByRevenue = valuePerWeight * selectedPkg.weight;

        // So sánh với mức tối thiểu đảm bảo
        const monthlyReceived = Math.max(profitByRevenue, selectedPkg.value * selectedPkg.minPercent);
        const yearlyProfit = monthlyReceived * 12;
        const totalProfit = monthlyReceived * DURATION;
        const totalReturn = totalProfit + selectedPkg.value;

        return {
            sharePool,
            valuePerWeight,
            profitByRevenue,
            monthlyReceived,
            yearlyProfit,
            totalProfit,
            totalReturn
        };
    }, [pkgIndex, selectedPkg]);

    const formatMillion = (val: number) => (val / 1000000).toFixed(0);
    const formatBillion = (val: number) => (val / 1000000000).toFixed(1);

    return (
        <div className="!bg-white dark:!bg-main-dark rounded-md p-6">
            <h3 className="!text-xl !font-semibold !text-gray-900 dark:!text-white mb-4">
                Công cụ tính lợi nhuận
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-gray-100 dark:bg-main-dark p-1.5 rounded-sm">

                {/* Left - Input (Slider chọn gói) */}
                <div className="space-y-6 bg-white dark:bg-dark-bg p-4 rounded-md">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="!text-sm !text-gray-900 dark:!text-gray-200">
                                Chọn gói đầu tư phù hợp
                            </span>
                            <span className="!text-lg !font-bold !text-primary dark:!text-blue-400">
                                {selectedPkg.name} - {(selectedPkg.value) > 1000000000 ? formatBillion(selectedPkg.value) : formatMillion(selectedPkg.value)} {(selectedPkg.value) > 1000000000 ? 'tỷ' : 'triệu'}
                            </span>
                        </div>

                        <input
                            type="range"
                            min="0"
                            max={PACKAGES.length - 1}
                            step="1"
                            value={pkgIndex}
                            onChange={(e) => setPkgIndex(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-md appearance-none cursor-pointer"
                            style={{
                                background: `linear-gradient(to right, #155dfb ${(pkgIndex / (PACKAGES.length - 1)) * 100}%, #e5e7eb ${(pkgIndex / (PACKAGES.length - 1)) * 100}%)`
                            }}
                        />

                        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                            {PACKAGES.map((pkg, idx) => (
                                <button
                                    key={pkg.name}
                                    onClick={() => setPkgIndex(idx)}
                                    className={`py-2 px-1 rounded-md text-[10px] font-bold uppercase transition-all border ${pkgIndex === idx
                                        ? 'bg-primary text-white border-primary shadow-md'
                                        : 'bg-white dark:bg-dark-card-elevated text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                        }`}
                                >
                                    {pkg.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Calculation Breakdown */}
                    <div className="p-4 space-y-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                            <span className="w-1 h-4 bg-primary rounded-full"></span> Cách tính dự kiến
                        </h4>

                        {/* Phần thực nhận */}
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">→</span>
                            <span className="text-gray-600 dark:text-gray-400">Phần thực nhận</span>
                            <span className="flex-1 border-b border-dashed border-gray-300 dark:border-gray-600 mx-2"></span>
                            <span className="font-medium text-gray-900 dark:text-white">
                                {selectedPkg.weight} / {TOTAL_WEIGHT}
                            </span>
                        </div>

                        {/* Quỹ chia sẻ 8% */}
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">→</span>
                            <span className="text-gray-600 dark:text-gray-400">Lợi nhuận chia thưởng </span>
                            <span className="flex-1 border-b border-dashed border-gray-300 dark:border-gray-600 mx-2"></span>
                            <span className="font-medium text-gray-900 dark:text-white">
                                8% Doanh thu
                            </span>
                        </div>

                        {/* Mức tối thiểu đảm bảo tính theo % giá trị gói */}
                        <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-3 border border-blue-100 dark:border-blue-900/30 shadow-sm">
                                <div className="flex justify-between items-start mb-1">
                                    <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wider">
                                        Mức phân bổ tối thiểu dự kiến ({selectedPkg.name}) ({selectedPkg.weight} weight)
                                    </p>
                                    <span className="text-[10px] bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 px-1.5 py-0.5 rounded font-bold">
                                        {selectedPkg.minPercent * 100}%/vốn
                                    </span>
                                </div>
                                <p className="text-lg font-black text-primary dark:text-blue-400">
                                    ~{formatMillion(selectedPkg.value * selectedPkg.minPercent)} triệu/tháng
                                </p>
                                <p className="text-[10px] text-blue-500/70 dark:text-blue-400/50 italic mt-1">
                                    * Áp dụng khi lợi nhuận doanh thu thấp hơn mức cam kết
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right - Results */}
                <div className="space-y-4 flex flex-col justify-center">
                    <div className="p-4 border-b border-gray-300 dark:border-gray-600">
                        <p className="text-xs text-gray-800 dark:text-gray-200 mb-1 uppercase font-bold tracking-wider">Thu nhập chịu thuế dự kiến (theo kịch bản phân bổ tối thiểu):</p>
                        <p className="text-4xl font-black text-primary dark:text-white mb-1">
                           ~ {formatMillion(calculations.monthlyReceived)} triệu
                        </p>
                        <p className="text-xs text-gray-500 italic">Mức thu nhập tối thiểu chưa trừ thuế</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 border-b border-gray-300 dark:border-gray-600">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Thu nhập/năm (theo mức tối thiểu)</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{formatMillion(calculations.yearlyProfit)} triệu</p>
                        </div>
                        {/* <div className="p-3 border-b border-gray-300 dark:border-gray-600">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tổng lãi dự tính trước thuế ({DURATION} tháng)</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{formatBillion(calculations.totalProfit)} tỷ</p>
                        </div> */}
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-dark-card-elevated rounded-lg border border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-bold uppercase">Tổng giá trị dự kiến sau {DURATION} tháng (theo kịch bản doanh thu tối thiểu – không phải cam kết) Giá trị thực tế phụ thuộc doanh thu ròng của dự án theo từng kỳ </p>
                        <span className="flex flex-row items-baseline mb-0">
                            <p className="text-3xl font-black text-primary dark:text-white mb-3 tracking-tighter">
                                {calculations.totalReturn > 1000000000 ? formatBillion(calculations.totalReturn) : formatMillion(calculations.totalReturn)} {calculations.totalReturn > 1000000000 ? 'tỷ' : 'triệu'}
                            </p>
                            <p className="ml-2 text-xs text-gray-500 dark:text-gray-400 ">(chưa bao gồm thuế)</p>
                        </span>
                        <div className="grid grid-cols-2 gap-3 text-sm border-t border-gray-300 dark:border-gray-700 pt-3 font-medium">
                            <div>
                                <p className="text-gray-500">Vốn gốc hoàn lại</p>
                                <p className="text-gray-900 dark:text-white">{selectedPkg.value > 1000000000 ? formatBillion(selectedPkg.value) : formatMillion(selectedPkg.value)} {selectedPkg.value > 1000000000 ? 'tỷ' : 'triệu'}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Tổng lợi nhuận trước thuế</p>
                                <p className="text-emerald-600 dark:text-emerald-400 font-bold">{calculations.totalProfit > 1000000000 ? formatBillion(calculations.totalProfit) : formatMillion(calculations.totalProfit)} {calculations.totalProfit > 1000000000 ? 'tỷ' : 'triệu'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfitCalculator;